import { createPortal } from "react-dom";

/* Reusable bulk-delete confirmation. Renders a centered modal dialog via
 * portal so it escapes any backdrop-filter containing block.
 *
 * Props:
 *  - count: number of records selected
 *  - lockedRecords: optional array of records that cannot be deleted
 *      (e.g. bills/invoices with payments applied). Each item should have
 *      `id` and a `label` describing it. When present, the dialog shows a
 *      warning listing them and disables the Delete button if ALL selected
 *      records are locked; otherwise Delete is enabled and the caller
 *      filters them out before deleting.
 *  - onConfirm / onCancel
 *  - lang: "en" | "es"
 */

var T = {
  en: {
    title: "Delete selected records",
    msg: "Are you sure you want to delete the selected records?",
    lockedTitle: "Some records cannot be deleted",
    lockedHint: "These records have payments applied. Delete the payments first.",
    cancel: "Cancel",
    confirm: "Delete",
    nothingToDelete: "All selected records have payments — nothing to delete.",
  },
  es: {
    title: "Eliminar registros seleccionados",
    msg: "¿Estás seguro de que quieres eliminar los registros seleccionados?",
    lockedTitle: "Algunos registros no pueden eliminarse",
    lockedHint: "Estos registros tienen pagos aplicados. Elimina primero los pagos.",
    cancel: "Cancelar",
    confirm: "Eliminar",
    nothingToDelete: "Todos los registros seleccionados tienen pagos — nada que eliminar.",
  },
};

export default function BulkDeleteDialog({ count, lockedRecords, onConfirm, onCancel, lang }) {
  var t = T[lang || "es"];
  var locked = lockedRecords || [];
  var deletable = Math.max(0, count - locked.length);
  var canDelete = deletable > 0;

  return createPortal(
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1400, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={onCancel}
    >
      <div
        onClick={function (e) { e.stopPropagation(); }}
        style={{ background: "var(--color-bg-card-solid)", color: "var(--color-text)", borderRadius: 10, maxWidth: 520, width: "100%", padding: 24, boxShadow: "0 16px 48px rgba(0,0,0,0.25)" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <span style={{ fontSize: 22 }}>{"⚠️"}</span>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#DC2626" }}>{t.title}</h3>
        </div>

        {canDelete && (
          <p style={{ margin: "0 0 12px", fontSize: 13, lineHeight: 1.5 }}>
            {lang === "es"
              ? "¿Estás seguro de que quieres eliminar " + deletable + " registro" + (deletable === 1 ? "" : "s") + " seleccionado" + (deletable === 1 ? "" : "s") + "?"
              : "Are you sure you want to delete " + deletable + " selected record" + (deletable === 1 ? "" : "s") + "?"}
          </p>
        )}

        {locked.length > 0 && (
          <div style={{ marginTop: 4, marginBottom: 14, padding: 12, borderRadius: 8, background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#B45309", marginBottom: 6 }}>{t.lockedTitle}</div>
            <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginBottom: 8 }}>{t.lockedHint}</div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: "var(--color-text-secondary)", maxHeight: 160, overflowY: "auto" }}>
              {locked.map(function (r, i) {
                return <li key={i} style={{ fontFamily: "'JetBrains Mono', monospace", marginBottom: 2 }}>{r.label || r.id}</li>;
              })}
            </ul>
            {!canDelete && (
              <div style={{ marginTop: 8, fontSize: 11, color: "#B45309", fontStyle: "italic" }}>{t.nothingToDelete}</div>
            )}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 6 }}>
          <button
            onClick={onCancel}
            style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 13, fontWeight: 500 }}
          >
            {t.cancel}
          </button>
          <button
            onClick={canDelete ? onConfirm : undefined}
            disabled={!canDelete}
            style={{ padding: "8px 22px", borderRadius: 6, border: "none", background: canDelete ? "#DC2626" : "var(--color-border)", color: "#fff", cursor: canDelete ? "pointer" : "not-allowed", fontSize: 13, fontWeight: 700, opacity: canDelete ? 1 : 0.6 }}
          >
            {t.confirm}{canDelete ? " (" + deletable + ")" : ""}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
